import { useState, useEffect } from "react";
import { NavLink, Link } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";
import { NAV_LINKS } from "@/lib/data";
import Logo from "@/components/Logo";

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/95 backdrop-blur-xl shadow-[0_1px_0_rgba(15,23,42,0.04)]" : "bg-white/85 backdrop-blur-md"
      }`}
    >
      <div className="zt-container flex items-center justify-between h-24">
        <Link to="/" data-testid="header-logo" aria-label="ZT Health home" className="group">
          <Logo />
        </Link>

        <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `relative px-4 py-2 text-sm font-medium transition-colors ${
                  isActive ? "text-slate-900" : "text-slate-600 hover:text-slate-900"
                } group`
              }
            >
              {({ isActive }) => (
                <>
                  {l.label}
                  <span
                    className={`absolute left-4 right-4 -bottom-0.5 h-px transition-all duration-300 ${
                      isActive ? "bg-slate-900 scale-x-100" : "bg-slate-900 scale-x-0 group-hover:scale-x-100"
                    }`}
                  />
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <button
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
          className="lg:hidden p-2 text-slate-900"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-slate-200 bg-white" data-testid="mobile-nav">
          <div className="zt-container py-4 flex flex-col">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `py-3 text-base border-b border-slate-100 ${isActive ? "text-slate-900 font-semibold" : "text-slate-600"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </div>
        </div>
      )}
      {/* Brand color divider bar */}
      <div
        data-testid="header-divider"
        aria-hidden="true"
        className="h-[6px] w-full"
        style={{ background: "var(--zt-teal)" }}
      />
    </header>
  );
}
